import { type NodeViewProps, NodeViewWrapper } from '@tiptap/react';
import { memo, useCallback } from 'react';

import { PrivateWorkspaceAgentComposerAssetPill } from '~/components/v2/components/workspace/workspace-agent-panel/tiptap/PrivateWorkspaceAgentComposerAssetPill';
import { PrivateWorkspaceAgentComposerBoardPill } from '~/components/v2/components/workspace/workspace-agent-panel/tiptap/PrivateWorkspaceAgentComposerBoardPill';
import { WorkspaceAgentComposerContextSourceFieldValuePill } from '~/components/v2/components/workspace/workspace-agent-panel/tiptap/WorkspaceAgentComposerContextSourceFieldValuePill';
import { readWorkspaceAgentPillNodeAttrs } from '~/components/v2/components/workspace/workspace-agent-panel/tiptap/workspaceAgentPill';

export type PrivateWorkspaceAgentPillNodeViewProps = Pick<
  NodeViewProps,
  'deleteNode' | 'editor' | 'getPos' | 'node' | 'selected'
>;

/** React node view for the composer pill atom: picks the asset, board or brand kit chip from the node's attrs and hands focus back to the editor on close or remove. */
export const PrivateWorkspaceAgentPillNodeView = memo(
  ({ deleteNode, editor, getPos, node, selected }: PrivateWorkspaceAgentPillNodeViewProps) => {
    const attrs = readWorkspaceAgentPillNodeAttrs(node.attrs);

    /**
     * A closed card hands the caret back: the chip is a `contentEditable={false}` island a keyboard user
     * cannot escape. `view.focus()`, not the command — the editor is unfocused, which throws in TipTap.
     * Radix's close autofocus runs a tick after the layer goes, and the remove button may have deleted
     * this node in the meantime: whatever holds that position now is another chip, and selecting it would
     * drop the caret on a reference the user never touched.
     */
    const handleRestoreEditorFocus = useCallback(() => {
      const position = getPos();

      if (editor.isDestroyed || typeof position !== 'number') {
        return;
      }

      if (!editor.state.doc.nodeAt(position)?.sameMarkup(node)) {
        return;
      }

      editor.view.focus();
      editor.commands.setNodeSelection(position);
    }, [editor, getPos, node]);

    const handleRemove = useCallback(() => {
      deleteNode();

      if (!editor.isDestroyed) {
        editor.view.focus();
      }
    }, [deleteNode, editor]);

    // Attrs that are not a usable reference render nothing: the serializer would refuse the node anyway.
    if (!attrs) {
      return null;
    }

    const { assetType, ext, id, label, thumbnailUrl, type, versionId } = attrs;

    // Branching on `attrs` rather than the destructured `type` is what narrows the reference fields —
    // the reader refuses a board node with no `boardId`, and a kit node missing either of its ids.
    let chip = (
      <PrivateWorkspaceAgentComposerAssetPill
        // The attr is optional on a pill minted before it existed; the chip wants one answer.
        assetType={assetType}
        ext={ext}
        isSelected={selected}
        label={label}
        onCardClose={handleRestoreEditorFocus}
        onRemove={handleRemove}
        thumbnailUrl={thumbnailUrl}
        versionId={versionId}
      />
    );

    if (attrs.type === 'board') {
      chip = (
        <PrivateWorkspaceAgentComposerBoardPill
          boardId={attrs.boardId}
          isSelected={selected}
          label={label}
          onCardClose={handleRestoreEditorFocus}
          onRemove={handleRemove}
          thumbnailUrl={thumbnailUrl}
        />
      );
    } else if (attrs.type === 'contextSourceFieldValue') {
      chip = (
        <WorkspaceAgentComposerContextSourceFieldValuePill
          isSelected={selected}
          label={label}
          onRemove={handleRemove}
          thumbnailUrl={thumbnailUrl}
        />
      );
    }

    return (
      <NodeViewWrapper
        as="span"
        className="inline-flex max-w-full align-middle"
        contentEditable={false}
        data-testid="WORKSPACE_AGENT_PILL"
        // Two chips for one reference carry the same id; the serializer collapses them to one pill.
        data-workspace-agent-pill-id={id}
        data-workspace-agent-pill-kind={type}
      >
        {chip}
      </NodeViewWrapper>
    );
  },
);

PrivateWorkspaceAgentPillNodeView.displayName = 'PrivateWorkspaceAgentPillNodeView';
